import { useQuery } from '@tanstack/react-query';
import { integrityApi } from '../../services/enterpriseApi';
import Card from '../../components/common/Card';
import { LoadingScreen } from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import {
  Shield,
  ShieldAlert,
  CheckCircle,
  XCircle,
  Link as LinkIcon,
  Clock,
  AlertTriangle,
} from 'lucide-react';

export default function IntegrityDashboard() {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['integrity-stats'],
    queryFn: () => integrityApi.getStats(),
  });

  const { data: tamperAlerts, isLoading: alertsLoading } = useQuery({
    queryKey: ['tamper-alerts'],
    queryFn: () => integrityApi.getTamperAlerts({ limit: 10 }),
  });

  const { data: chainVerification, isLoading: chainLoading } = useQuery({
    queryKey: ['chain-verification'],
    queryFn: () => integrityApi.verifyChain(),
    refetchInterval: 60000, // Refresh every minute
  });

  if (statsLoading || alertsLoading || chainLoading) {
    return <LoadingScreen message="Loading integrity dashboard..." />;
  }

  const statCards = [
    {
      title: 'Total Seals',
      value: stats?.data?.total_seals || 0,
      icon: Shield,
      color: 'bg-primary-500',
      description: 'Cryptographic seals created',
    },
    {
      title: 'Verified Seals',
      value: stats?.data?.verified_seals || 0,
      icon: CheckCircle,
      color: 'bg-green-500',
      description: 'Integrity confirmed',
    },
    {
      title: 'Tamper Alerts',
      value: stats?.data?.tamper_alerts || 0,
      icon: ShieldAlert,
      color: 'bg-red-500',
      description: 'Potential tampering detected',
    },
    {
      title: 'Chain Integrity',
      value: chainVerification?.data?.is_valid ? '100%' : 'BROKEN',
      icon: LinkIcon,
      color: chainVerification?.data?.is_valid ? 'bg-green-500' : 'bg-red-500',
      description: 'Blockchain-like chain status',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-display font-bold text-gray-900 dark:text-white">
          Data Integrity Dashboard
        </h1>
        <p className="text-gray-600 dark:text-gray-300 mt-1">
          SHA-256 Digital Seal Engine - Cryptographic data integrity monitoring
        </p>
      </div>

      {/* Chain Status Alert */}
      {chainVerification?.data && !chainVerification.data.is_valid && (
        <Alert type="error" title="Chain Integrity Compromised">
          <div className="space-y-2">
            <p>The seal chain has been broken. This indicates potential tampering.</p>
            <p className="text-sm">
              <strong>Broken at:</strong> Seal #{chainVerification.data.broken_at_index}
            </p>
            <p className="text-sm">
              <strong>Total seals checked:</strong> {chainVerification.data.total_seals}
            </p>
          </div>
        </Alert>
      )}

      {chainVerification?.data?.is_valid && (
        <Alert type="success" title="Chain Integrity Verified">
          All {chainVerification.data.total_seals} seals in the chain are valid and unbroken.
        </Alert>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{stat.title}</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stat.value}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{stat.description}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Tamper Alerts */}
      <Card>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Recent Tamper Alerts</h2>
          </div>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Last 10 alerts
          </span>
        </div>

        {tamperAlerts?.data?.alerts && tamperAlerts.data.alerts.length > 0 ? (
          <div className="space-y-4">
            {tamperAlerts.data.alerts.map((alert: any) => (
              <div
                key={alert.id}
                className="p-4 border-l-4 border-red-500 bg-red-50 rounded-r-lg"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <ShieldAlert className="w-4 h-4 text-red-600" />
                      <h3 className="font-semibold text-red-900">
                        {alert.record_type.toUpperCase()} - {alert.record_id}
                      </h3>
                    </div>
                    <p className="text-sm text-red-800 mb-2">{alert.message}</p>
                    <div className="grid grid-cols-2 gap-4 text-xs text-red-700">
                      <div>
                        <span className="font-medium">Expected Hash:</span>
                        <p className="font-mono mt-1 break-all">{alert.expected_hash}</p>
                      </div>
                      <div>
                        <span className="font-medium">Actual Hash:</span>
                        <p className="font-mono mt-1 break-all">{alert.actual_hash}</p>
                      </div>
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <div className="flex items-center text-xs text-red-600">
                      <Clock className="w-3 h-3 mr-1" />
                      {new Date(alert.detected_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-300 mb-2">No tamper alerts detected</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              All data seals are intact and verified
            </p>
          </div>
        )}
      </Card>

      {/* Seal Statistics by Type */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Seals by Record Type
          </h3>
          <div className="space-y-3">
            {stats?.data?.by_record_type &&
              Object.entries(stats.data.by_record_type).map(([type, count]: [string, any]) => (
                <div key={type} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                    {type}
                  </span>
                  <span className="text-sm font-bold text-gray-900 dark:text-white">{count}</span>
                </div>
              ))}
          </div>
        </Card>

        <Card>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Chain Information
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total Seals</span>
              <span className="text-sm font-bold text-gray-900 dark:text-white">
                {chainVerification?.data?.total_seals || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Chain Status</span>
              <span
                className={`text-sm font-bold ${
                  chainVerification?.data?.is_valid ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {chainVerification?.data?.is_valid ? 'VALID' : 'BROKEN'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Last Verified</span>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {new Date().toLocaleTimeString()}
              </span>
            </div>
          </div>
        </Card>
      </div>

      {/* How It Works */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          How SHA-256 Digital Seal Works
        </h3>
        <div className="prose prose-sm max-w-none text-gray-600 dark:text-gray-300">
          <ol className="space-y-2">
            <li>
              <strong>Data Ingestion:</strong> When data enters the system (mission, analysis,
              decision), a SHA-256 hash is generated.
            </li>
            <li>
              <strong>Seal Creation:</strong> The hash is stored in an immutable ledger with a
              link to the previous seal (blockchain-like chain).
            </li>
            <li>
              <strong>Verification:</strong> Any attempt to modify the data will change its hash,
              breaking the seal and triggering a tamper alert.
            </li>
            <li>
              <strong>Chain Integrity:</strong> The entire chain is periodically verified to
              ensure no seals have been compromised.
            </li>
          </ol>
          <p className="mt-4 text-sm">
            This cryptographic approach ensures data integrity for compliance, legal claims, and
            audit requirements.
          </p>
        </div>
      </Card>
    </div>
  );
}

// Made with Bob
